import React, { useState } from 'react';
import { Globe, Loader2 } from 'lucide-react';
import { getWebsiteScreenshot } from '../services/scrapingBee';
import { extractTextFromImage } from '../services/textExtraction';
import { analyzeCopyWithGPT4 } from '../services/openai';
import { ErrorMessage } from './ErrorMessage';
import { CopyAnalysisTable } from './CopyAnalysisTable';
import { CopyAnalysisResult } from '../types/copyAnalysis';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useAuthContext } from '@/contexts/AuthContext';
import { saveAnalysis } from '@/lib/db/analysis';

interface GeneratorFormProps {
  onAnalysisStart: () => void;
  onAnalysisComplete: (result: CopyAnalysisResult | null) => void;
  initialAnalysis?: CopyAnalysisResult | null;
}

export function GeneratorForm({ 
  onAnalysisStart, 
  onAnalysisComplete,
  initialAnalysis = null 
}: GeneratorFormProps) {
  const { user } = useAuthContext();
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<CopyAnalysisResult | null>(initialAnalysis);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setAnalysis(null);
    onAnalysisStart();

    try {
      const urlObj = new URL(url);
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        throw new Error('Please enter a valid HTTP or HTTPS URL');
      }

      const screenshotUrl = await getWebsiteScreenshot(url);
      const extractedText = await extractTextFromImage(screenshotUrl);
      
      if (!extractedText?.trim()) {
        throw new Error('No text could be extracted from the website');
      }

      const analysisResult = await analyzeCopyWithGPT4(extractedText);
      
      if (user) {
        await saveAnalysis(user.uid, url, analysisResult);
      }

      setAnalysis(analysisResult);
      onAnalysisComplete(analysisResult);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze website. Please try again.';
      setError(errorMessage);
      console.error('Analysis error:', err);
      onAnalysisComplete(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col w-full max-w-7xl">
      {!analysis && (
        <form onSubmit={handleSubmit} className="w-full max-w-2xl mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-grow">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Globe className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                id="url-input"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Enter your website URL"
                className="pl-10 h-12 text-base transition-all duration-300"
                required
                disabled={isLoading}
                pattern="https?://.*"
              />
            </div>
            <Button
              type="submit"
              variant="gradient"
              size="lg"
              disabled={isLoading}
              className="h-12 px-8 text-base sm:w-auto w-full"
            >
              {isLoading && <Loader2 className="h-5 w-5 animate-spin mr-2" />}
              {isLoading ? 'Analyzing...' : 'Analyze Copy'}
            </Button>
          </div>
        </form>
      )}

      <ErrorMessage error={error} />
      {analysis && <CopyAnalysisTable analysis={analysis} />}
    </div>
  );
}